import { isNotUndefined } from "Backend";
import {
  CalcCaseProperty,
  CaseDisplayGroup,
  CaseDisplayGroupType,
  CustomCaseProperty,
  CustomDocumentProperty,
  CustomPropertyNameMatcher,
  CustomPropertyType,
  CustomPropertyVisibility,
} from "generated/graphql";
import { stripNull } from "helpers/coll";

export function compareCustomProperty(a?: { key: string } | null, b?: { key: string } | null): boolean {
  return a?.key === b?.key;
}

export interface ClipboardCalc {
  dataType: CustomPropertyType;
  displayName: string;
  isCollectVisible: boolean;
  key: string;
  selectOptions: string[];
  description?: string;
}

export interface ClipboardCasePropertyWithGroup {
  dataType: CustomPropertyType;
  displayName: string;
  description?: string;
  key: string;
  selectOptions: string[];
  isHidden: boolean;
  collectVisibility: CustomPropertyVisibility;
  caseDisplayGroupName: string;
}

export type ClipboardCaseProperty = Omit<ClipboardCasePropertyWithGroup, "caseDisplayGroupName">;

export interface ClipboardDocumentProperty {
  dataType: CustomPropertyType;
  displayName: string;
  key: string;
  selectOptions: string[];
  isHidden: boolean;
  preventsLensHits: boolean;
  nameMatchers: CustomPropertyNameMatcher[];
  collectVisibility: CustomPropertyVisibility;
}

export enum ClipboardDisplayGroupItemType {
  calc = "calc",
  property = "property",
}
export interface ClipboardDisplayGroupItem {
  type: ClipboardDisplayGroupItemType;
  key: string;
  rowIndex?: number;
  colIndex?: number;
}
export interface ClipboardDisplayGroup {
  name: string;
  type: CaseDisplayGroupType;
  rows?: string[];
  rowDescriptions?: string[];
  columns?: string[];
  columnDescriptions?: string[];
  items: ClipboardDisplayGroupItem[];
  description?: string;
}

export function isClipboardDisplayGroup(
  clipboardDisplayGroup: ClipboardDisplayGroup | null
): clipboardDisplayGroup is ClipboardDisplayGroup {
  return clipboardDisplayGroup !== null;
}

export const makeCalcForCopy: (calcCaseProperty: CalcCaseProperty) => ClipboardCalc = (calcCaseProperty) => {
  return {
    dataType: calcCaseProperty.dataType,
    displayName: calcCaseProperty.displayName,
    isCollectVisible: calcCaseProperty.isCollectVisible,
    key: calcCaseProperty.key,
    selectOptions: calcCaseProperty.selectOptions,
    description: calcCaseProperty.description || undefined,
  };
};

export const makeClipboardDisplayGroupsForCopy: (caseDisplayGroup: CaseDisplayGroup) => ClipboardDisplayGroup = (
  caseDisplayGroup
) => {
  return {
    name: caseDisplayGroup.name,
    description: stripNull(caseDisplayGroup.description),
    rows: stripNull(caseDisplayGroup.rows),
    columns: stripNull(caseDisplayGroup.columns),
    rowDescriptions: stripNull(caseDisplayGroup.rowDescriptions),
    columnDescriptions: stripNull(caseDisplayGroup.columnDescriptions),
    type: caseDisplayGroup.type,
    items: caseDisplayGroup.items
      .map((item) => {
        return item.caseCalcKey
          ? {
              type: ClipboardDisplayGroupItemType.calc,
              key: item.caseCalcKey,
              rowIndex: stripNull(item.rowIndex),
              colIndex: stripNull(item.colIndex),
            }
          : item.casePropertyKey
            ? {
                type: ClipboardDisplayGroupItemType.property,
                key: item.casePropertyKey,
                rowIndex: stripNull(item.rowIndex),
                colIndex: stripNull(item.colIndex),
              }
            : undefined;
      })
      .filter(isNotUndefined),
  };
};

export const makeCasePropertyForCopy: (caseProperty: CustomCaseProperty) => ClipboardCaseProperty = (caseProperty) => ({
  dataType: caseProperty.dataType,
  displayName: caseProperty.displayName,
  description: caseProperty.description || undefined,
  key: caseProperty.key,
  selectOptions: caseProperty.selectOptions,
  isHidden: caseProperty.isHidden,
  collectVisibility: caseProperty.collectVisibility,
});

export const makeDocumentPropertyForCopy: (documentProperty: CustomDocumentProperty) => ClipboardDocumentProperty = (
  documentProperty
) => {
  return {
    dataType: documentProperty.dataType,
    displayName: documentProperty.displayName,
    key: documentProperty.key,
    selectOptions: documentProperty.selectOptions,
    isHidden: documentProperty.isHidden,
    preventsLensHits: documentProperty.preventsLensHits,
    nameMatchers: documentProperty.nameMatchers,
    collectVisibility: documentProperty.collectVisibility,
  };
};

export function onPropertyChecked<T extends { key: string }>(properties: T[], checkedProperty: T): T[] {
  if (properties.find((p) => p.key === checkedProperty.key)) {
    return properties.filter((p) => p.key !== checkedProperty.key);
  } else {
    return [...properties, checkedProperty];
  }
}
